import { FC, KeyboardEvent, useState } from 'react'
import { faArrowUpRightAndArrowDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, styled } from '@mui/material'
import { blueGrey } from '@mui/material/colors'

const RESIZE_STEP = 10

type Props = {
  onResize?: (widthDelta: number, heightDelta: number) => void
  width: number
  height: number
  minWidth: number
  minHeight: number
  maxWidth: number
  maxHeight: number
  label: string
}

export const CornerResizeHandle: FC<Props> = ({
  onResize,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  label,
}) => {
  const [lastAxis, setLastAxis] = useState<'width' | 'height'>('width')

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      onResize?.(RESIZE_STEP, 0)
      setLastAxis('width')
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      onResize?.(-RESIZE_STEP, 0)
      setLastAxis('width')
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      onResize?.(0, RESIZE_STEP)
      setLastAxis('height')
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      onResize?.(0, -RESIZE_STEP)
      setLastAxis('height')
    }
  }

  const isWidthAxis = lastAxis === 'width'
  const value = isWidthAxis ? width : height

  // a11y: announce the axis just resized; VoiceOver reads valuetext verbatim on each change.
  const valueText = `${Math.round(value)} pixels ${isWidthAxis ? 'width' : 'height'}`

  return (
    <StyledHandle
      role='slider'
      aria-label={label}
      aria-orientation={isWidthAxis ? 'horizontal' : 'vertical'}
      aria-valuemin={isWidthAxis ? minWidth : minHeight}
      aria-valuemax={isWidthAxis ? maxWidth : maxHeight}
      aria-valuenow={value}
      aria-valuetext={valueText}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <StyledIcon icon={faArrowUpRightAndArrowDownLeftFromCenter} />
    </StyledHandle>
  )
}

const StyledHandle = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: blueGrey[100],
  color: blueGrey[500],
  cursor: 'nwse-resize',
}))

const StyledIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  transform: 'rotate(90deg)',
  fontSize: theme.typography.body2.fontSize,
}))
